/**
 * Erklärtexte für Fachbegriffe.
 *
 * Jeder Eintrag hat genau drei Teile, und das ist der Kern der Sache:
 *
 *   what — was es ist, in Alltagssprache
 *   why  — warum es zählt, mit spürbarer Folge
 *   todo — was zu tun ist
 *
 * Die Dreiteilung ist erzwungen statt empfohlen. Ein freies Textfeld füllt
 * sich nach zwei Wochen mit Fachsprache; drei benannte Felder zwingen dazu,
 * die Folge zu benennen und einen Rat zu geben.
 *
 * Verboten ist, ein Fachwort mit einem anderen Fachwort zu erklären. „SPF ist
 * ein DNS-TXT-Eintrag zur Absenderautorisierung" ist keine Erklärung — es ist
 * dieselbe Aussage in schwerer.
 *
 * Alle Texte stehen hier zusammen und nicht verstreut in den Seiten. Sonst
 * steht derselbe Begriff an drei Stellen verschieden erklärt.
 */

export interface Erklaerung {
  term: string;
  what: string;
  why: string;
  todo: string;
}

export const ERKLAERUNGEN = {
  // --- Zustellbarkeit -------------------------------------------------------
  spf: {
    term: 'SPF',
    what: 'Ein Eintrag bei deinem Domainanbieter, der festlegt, welche Server in deinem Namen Mails verschicken dürfen.',
    why: 'Fehlt er, behandeln Gmail und Outlook deine Mails wie einen Brief ohne Absender — im besten Fall Spam-Ordner, im schlechteren gar keine Zustellung.',
    todo: 'Kopiere den vorgeschlagenen Eintrag und trage ihn bei deinem Domainanbieter ein.',
  },
  dkim: {
    term: 'DKIM',
    what: 'Eine unsichtbare Unterschrift, die jede deiner Mails unterwegs vor Veränderung schützt.',
    why: 'Ohne sie kann jeder Mails in deinem Namen verschicken, und Empfänger können echte nicht von gefälschten unterscheiden.',
    todo: 'Aktiviere DKIM bei deinem Versanddienst und trage den Eintrag ein, den er dir gibt.',
  },
  dmarc: {
    term: 'DMARC',
    what: 'Deine Anweisung an Gmail und Outlook, was mit Mails geschehen soll, die vorgeben von dir zu sein, aber die Prüfung nicht bestehen.',
    why: 'Ohne diese Anweisung entscheidet jeder Anbieter selbst — und du erfährst nie, ob jemand deinen Namen missbraucht.',
    todo: 'Beginne mit „none", um nur zu beobachten, und verschärfe erst, wenn SPF und DKIM sauber sind.',
  },
  dnsbl: {
    term: 'Blockliste',
    what: 'Öffentliche Verzeichnisse von Servern, die als Spamschleudern aufgefallen sind.',
    why: 'Steht deine Versand-IP darauf, lehnen viele Empfänger deine Mails ab, bevor sie überhaupt ankommen.',
    todo: 'Bist du gelistet, folge dem Austragungsverfahren der jeweiligen Liste und sende bis dahin weniger.',
  },
  bounceRate: {
    term: 'Bounce-Rate',
    what: 'Der Anteil der Mails, die gar nicht zugestellt werden konnten.',
    why: 'Über fünf Prozent gilt bei Gmail als Zeichen für gekaufte oder veraltete Adressen — danach landet auch der Rest im Spam.',
    todo: 'Bleib unter zwei Prozent. Steigt der Wert, prüfe, woher die Adressen stammen.',
  },
  bounceHart: {
    term: 'Harter Bounce',
    what: 'Die Adresse existiert nicht — dauerhaft.',
    why: 'Weiter an sie zu senden schadet deinem Ruf bei jedem Versand erneut, ohne je jemanden zu erreichen.',
    todo: 'Nichts. Die Adresse wird automatisch gesperrt und aus künftigen Versänden genommen.',
  },
  bounceWeich: {
    term: 'Weicher Bounce',
    what: 'Vorübergehend nicht zustellbar — Postfach voll, Server gerade nicht erreichbar.',
    why: 'Solche Adressen zu sperren wäre falsch: sie funktionieren morgen meist wieder.',
    todo: 'Nichts. Häufen sich weiche Bounces bei derselben Adresse über Wochen, prüfe sie von Hand.',
  },
  beschwerde: {
    term: 'Beschwerde',
    what: 'Jemand hat deine Mail als Spam gemeldet.',
    why: 'Das wiegt weit schwerer als eine Abmeldung: Gmail merkt sich das für deine gesamte Domain, nicht nur für diesen Empfänger.',
    todo: 'Der Kontakt wird sofort gesperrt. Häufen sich Beschwerden, sende seltener und nur an Leute, die zugestimmt haben.',
  },
  absenderpool: {
    term: 'Absenderpool',
    what: 'Mehrere Absenderadressen, auf die der Versand verteilt wird.',
    why: 'Fällt eine Adresse aus, läuft der Versand weiter — und keine einzelne überschreitet ihr Tageslimit.',
    todo: 'Für den Anfang reicht ein Absender. Ab einigen tausend Mails am Tag lohnt ein zweiter.',
  },
  warmup: {
    term: 'Warmup',
    what: 'Neue Absenderadressen dürfen anfangs nur wenige Mails am Tag verschicken, danach täglich etwas mehr.',
    why: 'Wer sofort Tausende sendet, landet bei Gmail und Outlook im Spam-Ordner — und kommt da schwer wieder heraus.',
    todo: 'Lass es eingeschaltet, wenn die Adresse jünger als acht Wochen ist.',
  },
  sendefenster: {
    term: 'Sendefenster',
    what: 'Die Tageszeit, zu der überhaupt verschickt wird.',
    why: 'Mails, die nachts um drei ankommen, werden morgens mit fünfzig anderen zusammen gesichtet und eher gelöscht.',
    todo: 'Wähle die Bürozeiten deiner Empfänger, meist 8 bis 18 Uhr.',
  },
  notbremse: {
    term: 'Notbremse',
    what: 'Ein Absender wird automatisch angehalten, wenn zu viele seiner Mails nicht ankommen.',
    why: 'Ohne sie läuft eine Kampagne mit falschen Adressen bis zum Ende durch und ruiniert dabei den Ruf der Domain.',
    todo: 'Fünf Prozent sind ein guter Startwert. Sie greift erst ab fünfzig Mails, darunter ist die Rate zufällig.',
  },
  spamvorschau: {
    term: 'Spam-Vorschau',
    what: 'Eine Prüfung deines Entwurfs auf Dinge, die Spamfilter misstrauisch machen.',
    why: 'Sie ersetzt keinen echten Filter, fängt aber die häufigsten Fehler ab, bevor tausend Leute sie bekommen.',
    todo: 'Behebe alles, was als Fehler markiert ist. Warnungen sind Ermessenssache.',
  },
  ruecklauf: {
    term: 'Rücklaufkanal',
    what: 'Die Verbindung, über die dein Versanddienst dir meldet, welche Mails nicht angekommen sind.',
    why: 'Ohne ihn bleibt deine Bounce-Rate bei null, und die Notbremse kann nie auslösen, weil sie nichts zu messen hat.',
    todo: 'Erzeuge die Adresse hier und trage sie bei deinem Versanddienst als Webhook ein.',
  },

  // --- Messung --------------------------------------------------------------
  eindeutigeOeffnung: {
    term: 'Eindeutige Öffnung',
    what: 'Zählt jede Person einmal, auch wenn sie die Mail dreimal öffnet.',
    why: 'Ohne das sähe eine Kampagne erfolgreicher aus, als sie war.',
    todo: 'Nimm diese Zahl, wenn du wissen willst, wie viele Menschen du erreicht hast.',
  },
  maschinelleOeffnung: {
    term: 'Maschinelle Öffnung',
    what: 'Eine Öffnung, die kein Mensch ausgelöst hat, sondern ein Virenscanner oder der Bildzwischenspeicher eines Anbieters.',
    why: 'Mitgezählt würden sie deine Öffnungsrate um ein Vielfaches aufblähen — Werte über hundert Prozent sind so entstanden.',
    todo: 'Nichts. Sie werden erkannt und getrennt ausgewiesen, damit du siehst, wie viele es waren.',
  },
  klickrate: {
    term: 'Klickrate',
    what: 'Der Anteil der Empfänger, die auf einen Link in der Mail geklickt haben.',
    why: 'Sie sagt mehr über echtes Interesse als die Öffnungsrate, denn Öffnungen lassen sich kaum verlässlich messen.',
    todo: 'Vergleiche sie zwischen deinen Kampagnen, nicht mit fremden Zahlen — jede Branche liegt anders.',
  },
  zustellrate: {
    term: 'Zustellrate',
    what: 'Der Anteil der Mails, die vom Server des Empfängers angenommen wurden.',
    why: 'Angenommen heißt nicht im Posteingang — auch eine Mail im Spam-Ordner gilt als zugestellt.',
    todo: 'Unter 95 Prozent stimmt etwas mit deinen Adressen oder deinem Ruf nicht.',
  },
  abmelderate: {
    term: 'Abmelderate',
    what: 'Der Anteil der Empfänger, die sich nach dieser Mail abgemeldet haben.',
    why: 'Ein plötzlicher Anstieg ist das ehrlichste Zeichen dafür, dass Inhalt oder Häufigkeit nicht passen.',
    todo: 'Über ein halbes Prozent solltest du Anlass und Inhalt der Mail überdenken.',
  },
  abtest: {
    term: 'A/B-Test',
    what: 'Zwei Fassungen derselben Mail gehen an je einen Teil der Empfänger, die bessere an den Rest.',
    why: 'Ohne Test rätst du, welcher Betreff wirkt — und rätst bei jeder Kampagne neu.',
    todo: 'Ändere immer nur eine Sache. Bei zwei Unterschieden weißt du nicht, welcher gewirkt hat.',
  },
  signifikanz: {
    term: 'Signifikanz',
    what: 'Die Prüfung, ob ein Unterschied zwischen zwei Fassungen echt ist oder Zufall.',
    why: 'Bei kleinen Gruppen sieht fast immer eine Fassung besser aus, ohne dass es etwas bedeutet.',
    todo: 'Warte auf das Ergebnis der Prüfung, statt nach zwanzig Öffnungen einen Gewinner auszurufen.',
  },

  // --- Kontakte -------------------------------------------------------------
  segment: {
    term: 'Segment',
    what: 'Eine Gruppe von Kontakten, die du über Regeln beschreibst statt sie einzeln auszuwählen.',
    why: 'Die Gruppe hält sich selbst aktuell — wer die Bedingung erfüllt, kommt dazu, wer sie verliert, fällt heraus.',
    todo: 'Beginne mit einer Vorlage und passe sie an. Die Vorschau zeigt sofort, wie viele es trifft.',
  },
  segmentDynamisch: {
    term: 'Dynamisch oder fest',
    what: 'Ein dynamisches Segment prüft die Regel bei jedem Versand neu, eine feste Liste bleibt, wie sie ist.',
    why: 'Für „hat seit 90 Tagen nicht geöffnet" brauchst du das Erste — eine feste Liste wäre nach einer Woche falsch.',
    todo: 'Nimm im Zweifel das dynamische Segment. Feste Listen nur für einmalige Aktionen.',
  },
  unterdrueckung: {
    term: 'Unterdrückungsliste',
    what: 'Adressen, an die nie wieder gesendet wird — abgemeldet, gebounct oder von Hand gesperrt.',
    why: 'Sie wird vor jeder einzelnen Nachricht geprüft, damit auch eine laufende Kampagne niemanden erwischt, der sich gerade abgemeldet hat.',
    todo: 'Nichts. Einträge entstehen automatisch. Von Hand entfernen nur, wenn jemand ausdrücklich wieder möchte.',
  },
  doubleOptIn: {
    term: 'Double-Opt-In',
    what: 'Nach der Anmeldung kommt eine Mail mit Bestätigungslink; erst danach zählt die Anmeldung.',
    why: 'Es ist der einzige Nachweis, dass die Adresse wirklich der Person gehört, die sie eingetragen hat — und bei einer Beschwerde dein einziger Beleg.',
    todo: 'Lass es eingeschaltet. Der Verlust an Anmeldungen ist kleiner als der Schaden durch falsche Adressen.',
  },
  einwilligung: {
    term: 'Einwilligung',
    what: 'Der festgehaltene Nachweis, dass jemand Mails von dir bekommen wollte — mit Zeitpunkt und Herkunft.',
    why: 'Ohne diesen Nachweis ist Werbung per Mail in der Schweiz und der EU nicht zulässig.',
    todo: 'Importierst du Adressen, trage ein, woher sie stammen. „Unbekannt" ist ein Warnzeichen.',
  },
  lebenszyklus: {
    term: 'Lebenszyklus-Stufe',
    what: 'Wie weit ein Kontakt auf dem Weg vom Interessenten zum Kunden ist.',
    why: 'Sie erlaubt dir, Neulingen anderes zu schreiben als langjährigen Kunden.',
    todo: 'Setze sie bei wichtigen Kontakten von Hand oder lass sie durch einen Ablauf setzen.',
  },
  leadScore: {
    term: 'Lead-Score',
    what: 'Eine Punktzahl je Kontakt, die aus seinem Verhalten entsteht — Öffnen bringt Punkte, eine Beschwerde zieht ab.',
    why: 'Sie zeigt dir auf einen Blick, bei wem sich ein Anruf lohnt, ohne jede Historie zu lesen.',
    todo: 'Jeder Punkt bringt seine Begründung mit. Stimmt die Reihenfolge nicht, passe die Regeln an.',
  },
  dublette: {
    term: 'Dublette',
    what: 'Zwei Einträge, die vermutlich dieselbe Person oder Firma sind.',
    why: 'Unbemerkt bekommt dieselbe Person deine Mail doppelt — der schnellste Weg zu einer Spam-Meldung.',
    todo: 'Prüfe die Vorschläge und führe zusammen. Beim Zusammenführen geht nichts verloren.',
  },

  // --- Automatisierung ------------------------------------------------------
  ablauf: {
    term: 'Ablauf',
    what: 'Eine Kette von Schritten, die für jeden Kontakt einzeln abläuft — warten, prüfen, senden.',
    why: 'Damit passiert Nachfassen von selbst, statt dass du dir Termine notierst und sie vergisst.',
    todo: 'Beginne mit der Vorlage „Nachfass-Kette" und ändere nur die Texte.',
  },
  ausloeser: {
    term: 'Auslöser',
    what: 'Das Ereignis, das einen Kontakt in den Ablauf aufnimmt — etwa der Eintritt in eine Liste.',
    why: 'Ohne Auslöser passiert nichts; mit dem falschen bekommt der halbe Bestand ungewollt Post.',
    todo: 'Prüfe im Trockenlauf, wie viele Kontakte der Auslöser erfassen würde, bevor du scharf schaltest.',
  },
  bedingung: {
    term: 'Bedingung',
    what: 'Eine Weiche im Ablauf: erfüllt der Kontakt sie, geht es nach Ja weiter, sonst nach Nein.',
    why: 'Damit bekommt nur derjenige die zweite Mail, der die erste nicht geöffnet hat.',
    todo: 'Der Satz unter der Bedingung zeigt in Klartext, was geprüft wird. Lies ihn, bevor du startest.',
  },
  wartenknoten: {
    term: 'Warten',
    what: 'Eine Pause im Ablauf, bevor es weitergeht.',
    why: 'Ohne Pause käme die Nachfass-Mail Sekunden nach der ersten — das wirkt maschinell und wird gemeldet.',
    todo: 'Drei bis fünf Tage sind üblich. Kürzer wirkt drängend, länger wird vergessen.',
  },
  nachfassKette: {
    term: 'Nachfass-Kette',
    what: 'Mehrere Mails hintereinander, bei der jede nur an die geht, die auf die vorige nicht reagiert haben.',
    why: 'Der zweite Anlauf erreicht erfahrungsgemäß nochmal so viele Leute wie der erste — und stört niemanden, der schon geöffnet hat.',
    todo: 'Drei Stufen reichen. Wer nach der dritten nicht reagiert, reagiert auch nach der fünften nicht.',
  },
  trockenlauf: {
    term: 'Trockenlauf',
    what: 'Der Ablauf wird durchgerechnet, ohne dass eine Mail hinausgeht.',
    why: 'Er zeigt vorher, wie viele Kontakte wo landen — nach dem Start lässt sich eine versandte Mail nicht zurückholen.',
    todo: 'Immer einmal laufen lassen, bevor du einen Ablauf scharf schaltest.',
  },
  versionPinning: {
    term: 'Feste Fassung',
    what: 'Ein laufender Durchgang arbeitet weiter mit der Fassung des Ablaufs, mit der er begonnen hat.',
    why: 'Sonst würde eine Änderung mitten hinein Kontakte in Schritte springen lassen, die es beim Start nicht gab.',
    todo: 'Nichts. Änderungen gelten für neue Durchgänge; laufende kannst du beenden, wenn sie sollen.',
  },
  frequenzDeckel: {
    term: 'Frequenz-Deckel',
    what: 'Ein Mindestabstand zwischen zwei Mails an dieselbe Person.',
    why: 'Ohne ihn kann ein Kontakt an einem Tag aus Kampagne und Ablauf gleichzeitig Post bekommen.',
    todo: 'Sechzehn Stunden sind ein guter Wert — so kommt höchstens eine Mail pro Tag an.',
  },

  // --- CRM ------------------------------------------------------------------
  pipeline: {
    term: 'Pipeline',
    what: 'Der Weg, den ein Verkauf bei dir nimmt, in Stufen zerlegt.',
    why: 'Erst dadurch lässt sich sagen, wo etwas hängt, statt nur zu wissen, dass es hängt.',
    todo: 'Bilde deinen tatsächlichen Ablauf ab, nicht den, den du gern hättest. Fünf Stufen reichen meist.',
  },
  stufe: {
    term: 'Stufe',
    what: 'Ein Abschnitt in der Pipeline, etwa „Angebot verschickt".',
    why: 'Die Stufe bestimmt, wie wahrscheinlich der Abschluss ist und wann ein Deal als liegengeblieben gilt.',
    todo: 'Benenne sie nach dem, was passiert ist — nicht nach dem, was du vorhast.',
  },
  wahrscheinlichkeit: {
    term: 'Abschlusswahrscheinlichkeit',
    what: 'Wie viel Prozent der Deals aus dieser Stufe erfahrungsgemäß zum Abschluss kommen.',
    why: 'Sie ist die Grundlage des gewichteten Forecasts — mit falschen Werten ist der Forecast Zierde.',
    todo: 'Schätze zu Beginn und korrigiere nach einem halben Jahr anhand deiner echten Zahlen.',
  },
  forecast: {
    term: 'Gewichteter Forecast',
    what: 'Die Summe aller offenen Deals, jeder multipliziert mit der Wahrscheinlichkeit seiner Stufe.',
    why: 'Die rohe Summe aller Deals ist immer zu optimistisch; dieser Wert ist die ehrlichere Erwartung.',
    todo: 'Abgeschlossene Deals bleiben außen vor. Nutze ihn zum Planen, nicht als Zusage.',
  },
  liegengeblieben: {
    term: 'Liegengeblieben',
    what: 'Ein Deal, der zu lange in derselben Stufe steht.',
    why: 'Solche Deals sterben leise — niemand sagt ab, es meldet sich nur keiner mehr.',
    todo: 'Ruf an oder verschiebe ihn auf „verloren". Beides ist besser, als ihn stehen zu lassen.',
  },
} as const satisfies Record<string, Erklaerung>;

export type ErklaerungKey = keyof typeof ERKLAERUNGEN;
